import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, CheckCircle, ShieldCheck, Award, Clock } from 'lucide-react';
interface QuoteOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuoteOverlay({
  isOpen,
  onClose
}: QuoteOverlayProps) {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    enquiryType: '',
    message: '',
    consent: false
  });
  // Lock body scroll and handle Escape key
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);
  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormState('idle');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        enquiryType: '',
        message: '',
        consent: false
      });
    }
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setTimeout(() => setFormState('success'), 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const inputClass = 'w-full border border-gray-200 py-4 px-5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all bg-white rounded-xl';
  const labelClass = 'block text-xs font-bold uppercase tracking-widest text-primary-dark/50 mb-2 ml-1';
  return <AnimatePresence data-id="element-515">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-516">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} className="absolute inset-0 bg-primary-dark/80 backdrop-blur-md" onClick={onClose} data-id="element-517" />

          {/* Modal Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[95vh] flex flex-col" data-id="element-518">
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-6 right-6 z-20 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors text-gray-500 hover:text-primary-dark" aria-label="Close modal" data-id="element-519">
              <X size={20} data-id="element-520" />
            </button>

            <div className="overflow-y-auto" data-id="element-521">
              {formState === 'success' ? <div className="py-32 px-8 text-center" data-id="element-522">
                  <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-523">
                    <CheckCircle className="w-10 h-10 text-green-600" data-id="element-524" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-525">
                    Quote request received
                  </h3>
                  <p className="text-lg text-gray-600 max-w-md mx-auto mb-10" data-id="element-526">
                    Thank you. We've received your details and our commercial
                    team will be in touch shortly.
                  </p>
                  <button onClick={onClose} className="bg-primary-dark text-white px-10 py-4 rounded-full font-bold transition-colors" data-id="element-527">
                    Close Window
                  </button>
                </div> : <div className="grid lg:grid-cols-12" data-id="element-528">
                  {/* Left Side: Form */}
                  <div className="lg:col-span-7 p-8 md:p-12 lg:p-16" data-id="element-529">
                    <div className="mb-10" data-id="element-530">
                      <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4 tracking-tight" data-id="element-531">
                        Request a{' '}
                        <span className="text-accent italic" data-id="element-532">free quote</span>
                      </h2>
                      <p className="text-gray-500" data-id="element-533">
                        Get expert advice and a transparent proposal for your
                        commercial HVAC needs.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-534">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-535">
                        <div data-id="element-536">
                          <label htmlFor="firstName" className={labelClass} data-id="element-537">
                            First name *
                          </label>
                          <input id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleChange} className={inputClass} data-id="element-538" />
                        </div>
                        <div data-id="element-539">
                          <label htmlFor="lastName" className={labelClass} data-id="element-540">
                            Last name *
                          </label>
                          <input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleChange} className={inputClass} data-id="element-541" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-542">
                        <div data-id="element-543">
                          <label htmlFor="email" className={labelClass} data-id="element-544">
                            Work email *
                          </label>
                          <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={inputClass} data-id="element-545" />
                        </div>
                        <div data-id="element-546">
                          <label htmlFor="phone" className={labelClass} data-id="element-547">
                            Phone number *
                          </label>
                          <input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-548" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-549">
                        <div data-id="element-550">
                          <label htmlFor="company" className={labelClass} data-id="element-551">
                            Company name *
                          </label>
                          <input id="company" name="company" type="text" required value={formData.company} onChange={handleChange} className={inputClass} data-id="element-552" />
                        </div>

                        <div data-id="element-553">
                          <label htmlFor="enquiryType" className={labelClass} data-id="element-554">
                            Enquiry type *
                          </label>

                          <select id="enquiryType" name="enquiryType" required value={formData.enquiryType} onChange={handleChange} className={inputClass} data-id="element-555">
                            <option value="" data-id="element-556">Select type...</option>
                            <option value="new-installation" data-id="element-557">
                              New Installation
                            </option>

                            <option value="maintenance-service" data-id="element-558">
                              Maintenance & Service
                            </option>
                            <option value="repairs-breakdowns" data-id="element-559">
                              Repairs & Breakdowns
                            </option>
                            <option value="tm44-compliance" data-id="element-560">
                              TM44 Compliance
                            </option>
                            <option value="ventilation-systems" data-id="element-561">
                              Ventilation Systems
                            </option>
                            <option value="general-enquiry" data-id="element-562">
                              General Enquiry
                            </option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-563">
                        <label htmlFor="message" className={labelClass} data-id="element-564">
                          Project details
                        </label>
                        <textarea id="message" name="message" rows={3} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} data-id="element-565" />
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-4" data-id="element-566">
                        <div className="flex items-start gap-3 max-w-xs" data-id="element-567">
                          <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-gray-300 text-accent focus:ring-accent" data-id="element-568" />
                          <label htmlFor="consent" className="text-[10px] text-gray-400 leading-tight uppercase font-bold tracking-wider" data-id="element-569">
                            I agree to the{' '}
                            <a href="/privacy" className="underline" data-id="element-570">
                              Privacy Policy
                            </a>
                          </label>
                        </div>

                        <button type="submit" disabled={formState === 'loading'} className="bg-accent hover:bg-accent/90 disabled:opacity-50 text-white font-bold py-4 px-10 rounded-full transition-all shadow-lg shadow-accent/20 flex items-center justify-center gap-3" data-id="element-571">
                          {formState === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-572" /> : <span data-id="element-573">Submit Request</span>}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Right Side: Trust */}
                  <div className="lg:col-span-5 bg-gray-50 p-8 md:p-12 lg:p-16 flex flex-col justify-center" data-id="element-574">
                    <div className="space-y-12" data-id="element-575">
                      <div className="flex items-start gap-5" data-id="element-576">
                        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center shrink-0" data-id="element-577">
                          <Award className="w-6 h-6 text-accent" data-id="element-578" />
                        </div>
                        <div data-id="element-579">
                          <h4 className="font-bold text-primary-dark mb-1" data-id="element-580">
                            Daikin D1+ Partner
                          </h4>
                          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-581">
                            Extended warranties and priority manufacturer
                            support for all installations.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-5" data-id="element-582">
                        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center shrink-0" data-id="element-583">
                          <ShieldCheck className="w-6 h-6 text-accent" data-id="element-584" />
                        </div>
                        <div data-id="element-585">
                          <h4 className="font-bold text-primary-dark mb-1" data-id="element-586">
                            Gold Accredited
                          </h4>
                          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-587">
                            Constructionline Gold held by just 1-2% of HVAC
                            firms in the UK.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-5" data-id="element-588">
                        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center shrink-0" data-id="element-589">
                          <Clock className="w-6 h-6 text-accent" data-id="element-590" />
                        </div>
                        <div data-id="element-591">
                          <h4 className="font-bold text-primary-dark mb-1" data-id="element-592">
                            13+ Years Experience
                          </h4>
                          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-593">
                            Trusted nationwide for high-quality commercial
                            installations built to last.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-16 pt-8 border-t border-gray-200" data-id="element-594">
                      <div className="flex items-center gap-4 grayscale opacity-40" data-id="element-595">
                        <img src="https://cdn.magicpatterns.com/uploads/2vVokvhsbE3igxg2bfPash/d1.png" alt="Daikin" className="h-8 w-auto" data-id="element-596" />
                        <img src="https://cdn.magicpatterns.com/uploads/2FA87CtJq3CRcUi4Xcmf5w/acc-chas.png" alt="Constructionline" className="h-8 w-auto" data-id="element-597" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}